import { useEffect, useState, useCallback, useContext } from "react";
import Event from "./pages/event/Event";
import { BrowserRouter as Router, Route } from "react-router-dom";
import Auth from "./Auth/Auth";
import Callback from "./Callback";
import "mapbox-gl/dist/mapbox-gl.css";
import "./App.scss";
import authLocalDataService from "./services/data/authLocalDataService";
import { Context } from "./Context/LanguageContext";
import queryString from "query-string";
import { ThemeProvider } from "./Context/ThemeContext";
import useSessionTimeout from "./Hooks/useSessionTimeout";
import MLBFrame from "./components/MLBFrame/MLBFrame";

const App = (props) => {
  const auth = new Auth(props.history, props.location.pathname);
  const contextWrapper = useContext(Context);
  const [access_token, setAccessToken] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const [mlbSettings, setMlbSettings] = useState({
    showMlbHeaderFooter: false,
  });

  const updateMlbSettings = (settings) => {
    setMlbSettings(settings);
    document.documentElement.style.setProperty("--mlb-header-offset", "56px");
    document.documentElement.style.setProperty("--mlb-footer-offset", "281px");
  };

  const hasSessionTimedOut = useSessionTimeout(
    authLocalDataService.getExpiresAt()
  );

  useEffect(() => {
    window._paq = window._paq || [];
    
    window._paq.push(['setSecureCookie', true]);
    window._paq.push(['setRequestMethod', 'POST']);
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    window._paq.push(['enableHeartBeatTimer', 15]);
    
    const trackerUrl = 'https://matomo.yousmartthing.com/matomo.php';
    window._paq.push(['setTrackerUrl', trackerUrl]);
    window._paq.push(['setSiteId', process.env.REACT_APP_MATOMO_WEBSITE_ID]);
    
    // Performance tracking
    if (window.PerformanceObserver) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.entryType === 'navigation') {
              window._paq.push(['setCustomDimension', 1, entry.duration]);
              window._paq.push(['setCustomDimension', 2, entry.domInteractive]);
              window._paq.push(['setCustomDimension', 3, entry.domComplete]);
            }
          });
        });
        observer.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        console.error('Performance monitoring error:', e);
      }
    }
    
    // Error tracking
    const errorHandler = (event) => {
      const error = event.error || {};
      window._paq.push(['trackEvent', 'JavaScript', 'Error', 
        `${error.message || event.message} (${error.filename || 'unknown'}:${error.lineno || 0})`]);
    };
    
    const rejectionHandler = (event) => {
      let message = 'Unknown promise rejection';
      if (event.reason) {
        message = typeof event.reason === 'string' ? event.reason : (event.reason.message || 'Unknown error');
      }
      window._paq.push(['trackEvent', 'JavaScript', 'UnhandledRejection', message]);
    };
    
    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);
    
    // Matomo script for container
    const containerScript = document.createElement('script');
    containerScript.async = true;
    containerScript.defer = true;
    containerScript.src = `https://matomo.yousmartthing.com/js/container_${process.env.REACT_APP_MATOMO_CONTAINER_ID}.js`;
    document.head.appendChild(containerScript);
        
    // Cleanup function
    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);


  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (!document.querySelector(`script[src*="maps.googleapis.com"]`)) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        document.body.appendChild(script);
      }
    };

    loadGoogleMapsScript();

    const axiosModule = require("axios");
    const axios = axiosModule.default || axiosModule;
    const qs = require("qs");

    const url = process.env.REACT_APP_API_AUTH_URL;

    const data = {
      grant_type: "client_credentials",
      scope: "openid",
    };

    const auth = {
      username: process.env.REACT_APP_API_CLIENT_ID,
      password: process.env.REACT_APP_API_SECRET,
    };

    const options = {
      method: "post",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      data: qs.stringify(data),
      auth: auth,
      url,
    };

    axios(options)
      .then((response) => {
        const accessToken = response.data.access_token;
        const expiresAt =
          parseInt(response.data.expires_in) * 1000 + new Date().getTime();
        const prevIdToken = authLocalDataService.getIdToken();
        authLocalDataService.setAuth({
          accessToken,
          idToken: prevIdToken,
          expiresAt: expiresAt,
        });
        setAccessToken(accessToken);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  //Handles display:none and other render preventing measures when embedded. Prevents mounting until component has a height and width
  const refCallback = useCallback((ref) => {
    if (!ref || !ResizeObserver) {
      return;
    }
    //Resize observer needed because changes can be done outside of the iframe and react
    const resizeObserver = new ResizeObserver((mutations) => {
      if (
        mutations?.[0]?.contentRect?.height !== 0 &&
        mutations?.[0]?.contentRect?.width !== 0
      ) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    });

    const heldRef = ref;
    resizeObserver.observe(heldRef);
    return () => {
      resizeObserver.unobserve(heldRef);
    };
  }, []);

  return (
    <main ref={refCallback}>
      {mlbSettings.showMlbHeaderFooter && (
        <MLBFrame
          type="header"
          team={mlbSettings.mlbTeam}
          noLinks={mlbSettings.mlbNoLinks}
        />
      )}
      {hasSessionTimedOut && (
        <div className="overlay">
          Your session has expired. Please refresh the page or&nbsp;
          <span
            onClick={() => {
              window.location.reload();
            }}
            style={{ color: "blue", cursor: "pointer" }}
          >
            click here
          </span>
          .
        </div>
      )}
      <div className={hasSessionTimedOut ? "blurred" : ""}>
        <Router>
          <section>
            <Route
              exact
              path="/callback"
              render={(props) => (
                <Callback
                  auth={auth}
                  {...props}
                />
              )}
            />

            {access_token && isVisible && (
              <Route
                path="/"
                render={(props) => (
                  <ThemeProvider>
                    <Event
                      auth={auth}
                      contextWrapper={contextWrapper}
                      source={"event"}
                      apiToken={`Bearer ${access_token}`}
                      queryString={queryString.parse(props.location.search)}
                      initialUrlExtension={
                        props.location.pathname + props.location.search
                      }
                      updateMlbSettings={updateMlbSettings}
                      {...props}
                    />
                  </ThemeProvider>
                )}
              />
            )}
          </section>
        </Router>
      </div>
      {mlbSettings.showMlbHeaderFooter && (
        <MLBFrame
          type="footer"
          team={mlbSettings.mlbTeam}
          noLinks={mlbSettings.mlbNoLinks}
        />
      )}
    </main>
  );
};

export default App;
